import React from "react";
import { useFormik } from "formik";
import * as yup from "yup";

import {
  createMergeTokenYupTest,
  getInvitationTemplateLengthError,
  getInvitationTemplateMaxLength,
  getMergeTokenValidationError,
  INVITE_MERGE_TOKEN_FIELDS,
  INVITE_MERGE_TOKEN_KEYS,
  normalizeSingleBraceMergeTokens,
  type MergeTokenField,
} from "../../../utils";
import { Button, Checkbox, Sidebar } from "../../ui";

type MessageVariation = {
  id: string;
  label: string;
  message: string;
};

type ConnectionRequestFormValues = {
  isAiMessageEnabled: boolean;
  messageVariations: MessageVariation[];
  fallbackMessage: string;
  withdrawAfterDays: string;
  sendWithoutMessage: boolean;
};

export type ConnectionRequestFormPayload = {
  isAiMessageEnabled: boolean;
  fallbackMessage: string;
  withdrawAfterDays: number;
  messageVariations: MessageVariation[];
};

export type ConnectionRequestInitialConfig = Partial<ConnectionRequestFormPayload>;

type SequenceConnectionRequestSidebarProps = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (payload?: ConnectionRequestFormPayload) => void;
  initialConfig?: ConnectionRequestInitialConfig | null;
};

const FIELD_TOKENS: readonly MergeTokenField[] = INVITE_MERGE_TOKEN_FIELDS;

const INITIAL_VARIATIONS: MessageVariation[] = [
  { id: "message-a", label: "Message A", message: "Hi {{firstName}} - would love to connect" },
];

const validationSchema = yup.object({
  sendWithoutMessage: yup.boolean(),
  messageVariations: yup
    .array(
      yup.object({
        id: yup.string().required(),
        label: yup.string().required(),
        message: yup
          .string()
          .test("merge-tokens", "", createMergeTokenYupTest(INVITE_MERGE_TOKEN_KEYS) as yup.TestFunction),
      })
    )
    .min(1, "At least one message variation is required.")
    .required()
    .test("message-required", "Message text is required.", function (variations) {
      if (this.parent.sendWithoutMessage) return true;
      const msg = String(variations?.[0]?.message || "").trim();
      return msg.length > 0;
    })
    .test("message-max-length", function (variations) {
      if (this.parent.sendWithoutMessage) return true;
      const msg = String(variations?.[0]?.message || "");
      const lengthError = getInvitationTemplateLengthError(msg);
      if (!lengthError) return true;
      return this.createError({ message: lengthError });
    }),
});

function buildInitialValues(initialConfig?: ConnectionRequestInitialConfig | null): ConnectionRequestFormValues {
  const variationsFromConfig = initialConfig?.messageVariations;
  const messageVariations =
    Array.isArray(variationsFromConfig) && variationsFromConfig.length > 0
      ? variationsFromConfig
      : INITIAL_VARIATIONS;

  const primaryMessage = String(messageVariations[0]?.message || "").trim();
  const fallbackMessage = String(initialConfig?.fallbackMessage || "").trim();
  const sendWithoutMessage = !primaryMessage && !fallbackMessage;

  return {
    isAiMessageEnabled: Boolean(initialConfig?.isAiMessageEnabled),
    messageVariations,
    fallbackMessage: initialConfig?.fallbackMessage ?? "",
    withdrawAfterDays: String(initialConfig?.withdrawAfterDays ?? 25),
    sendWithoutMessage,
  };
}

export default function SequenceConnectionRequestSidebar({
  isOpen,
  onClose,
  onSave,
  initialConfig,
}: SequenceConnectionRequestSidebarProps) {
  const messageRef = React.useRef<HTMLTextAreaElement | null>(null);
  const messageBeforeSendWithoutRef = React.useRef<string | null>(null);

  const formik = useFormik<ConnectionRequestFormValues>({
    initialValues: buildInitialValues(initialConfig),
    enableReinitialize: true,
    validationSchema,
    validateOnMount: true,
    onSubmit: (values) => {
      const primary = values.messageVariations[0];
      const messageText = values.sendWithoutMessage
        ? ""
        : normalizeSingleBraceMergeTokens(primary?.message || "", INVITE_MERGE_TOKEN_KEYS);

      const payload: ConnectionRequestFormPayload = {
        isAiMessageEnabled: values.isAiMessageEnabled,
        fallbackMessage: values.sendWithoutMessage ? "" : String(values.fallbackMessage || "").trim(),
        withdrawAfterDays: Number(values.withdrawAfterDays) || 0,
        messageVariations: primary
          ? [
              {
                ...primary,
                message: messageText,
              },
            ]
          : [],
      };
      onSave(payload);
    },
  });

  React.useEffect(() => {
    if (isOpen) messageBeforeSendWithoutRef.current = null;
  }, [isOpen, initialConfig]);

  const sendWithoutMessage = formik.values.sendWithoutMessage;
  const primaryMessageText = String(formik.values.messageVariations[0]?.message ?? "");
  const templateMaxLength = React.useMemo(
    () => getInvitationTemplateMaxLength(primaryMessageText),
    [primaryMessageText]
  );

  const updatePrimaryMessage = (nextMessage: string) => {
    if (!formik.values.messageVariations[0] || sendWithoutMessage) return;
    const current = formik.values.messageVariations[0];
    const maxLength = getInvitationTemplateMaxLength(nextMessage);
    const cappedMessage = nextMessage.slice(0, maxLength);
    void formik.setFieldValue(
      "messageVariations",
      [{ ...current, message: cappedMessage }],
      true
    );
    void formik.setFieldTouched("messageVariations.0.message", true, false);
  };

  const primaryMessageLength = primaryMessageText.length;

  const formikMessageError = (() => {
    const variationErrors = formik.errors.messageVariations;
    if (typeof variationErrors === "string") return variationErrors;
    if (Array.isArray(variationErrors)) {
      const first = variationErrors[0];
      if (first && typeof first === "object" && typeof first.message === "string") {
        return first.message;
      }
    }
    return undefined;
  })();
  const liveMessageTokenError = sendWithoutMessage
    ? undefined
    : getMergeTokenValidationError(
        formik.values.messageVariations[0]?.message ?? "",
        INVITE_MERGE_TOKEN_KEYS
      );
  const liveLengthError = sendWithoutMessage
    ? undefined
    : getInvitationTemplateLengthError(primaryMessageText);
  const messageDisplayError = liveMessageTokenError || liveLengthError || formikMessageError;

  const handleSendWithoutMessageChange = (checked: boolean) => {
    formik.setFieldValue("sendWithoutMessage", checked, false);
    const current = formik.values.messageVariations[0];
    if (!current) {
      void formik.validateForm();
      return;
    }

    if (checked) {
      messageBeforeSendWithoutRef.current = current.message;
      void formik.setFieldValue("messageVariations", [{ ...current, message: "" }], false);
      formik.setFieldTouched("messageVariations.0.message", false, false);
    } else {
      const restored =
        String(messageBeforeSendWithoutRef.current || "").trim() || INITIAL_VARIATIONS[0].message;
      messageBeforeSendWithoutRef.current = null;
      void formik.setFieldValue("messageVariations", [{ ...current, message: restored }], false);
    }
    void formik.validateForm();
  };

  const insertToken = (tokenKey: string) => {
    if (sendWithoutMessage) return;

    const inner = String(tokenKey || "")
      .trim()
      .replace(/^\{\{?/, "")
      .replace(/\}\}?$/g, "");
    const tokenText = "{{" + inner + "}}";

    const currentMessage = formik.values.messageVariations[0]?.message || "";
    const start = messageRef.current?.selectionStart ?? currentMessage.length;
    const end = messageRef.current?.selectionEnd ?? currentMessage.length;
    const nextMessage = normalizeSingleBraceMergeTokens(
      `${currentMessage.slice(0, start)}${tokenText}${currentMessage.slice(end)}`,
      INVITE_MERGE_TOKEN_KEYS
    );
    updatePrimaryMessage(nextMessage);

    const nextCursor = start + tokenText.length;
    requestAnimationFrame(() => {
      if (!messageRef.current) return;
      messageRef.current.focus();
      messageRef.current.setSelectionRange(nextCursor, nextCursor);
    });
  };

  return (
    <Sidebar isOpen={isOpen} onClose={onClose} title="Connection request" size="medium">
      <form onSubmit={formik.handleSubmit} className="flex h-full flex-col">
        <div className="space-y-4 overflow-y-auto pr-1">
          <div className="rounded-[8px] border border-brand-blue/60 bg-bg-light-blue px-4 py-3 text-sm text-brand-blue">
            <p>
              LinkedIn&apos;s recent updates limit some non-premium accounts to sending only 5-10 personalized invites
              each month.
            </p>
            <p className="mt-2">
              If any of your sending accounts can&apos;t send a personalized note, the connection invite will be sent
              without a note.
            </p>
          </div>

          <Checkbox
            checked={sendWithoutMessage}
            onChange={handleSendWithoutMessageChange}
            label="Send without note"
            labelClassName="text-sm font-medium"
          />

          <div className="relative">
            {!sendWithoutMessage ? (
              <p
                className={[
                  "absolute right-3 top-2 text-xs",
                  primaryMessageLength > templateMaxLength ? "text-red-600" : "text-dark-grey",
                ].join(" ")}
              >
                {primaryMessageLength}/{templateMaxLength}
              </p>
            ) : null}
            <textarea
              ref={messageRef}
              disabled={sendWithoutMessage}
              maxLength={sendWithoutMessage ? undefined : templateMaxLength}
              className={[
                "min-h-[96px] w-full rounded-[8px] border border-light-grey px-3 py-2 text-sm outline-none focus:border-brand-blue focus:ring-1 focus:ring-brand-blue",
                sendWithoutMessage ? "cursor-not-allowed bg-background-grey text-dark-grey" : "pt-7",
              ]
                .filter(Boolean)
                .join(" ")}
              value={formik.values.messageVariations[0]?.message ?? ""}
              onChange={(event) => updatePrimaryMessage(event.target.value)}
              onBlur={() => {
                if (sendWithoutMessage) return;
                formik.setFieldTouched("messageVariations.0.message", true, true);
                const v = formik.values.messageVariations[0]?.message ?? "";
                const n = normalizeSingleBraceMergeTokens(v, INVITE_MERGE_TOKEN_KEYS);
                if (n !== v) formik.setFieldValue("messageVariations[0].message", n, true);
              }}
              placeholder={
                sendWithoutMessage
                  ? "Invitation will be sent without a personalized note."
                  : "Write your connection request message..."
              }
            />
            {!sendWithoutMessage && messageDisplayError ? (
              <p className="mt-1 text-xs text-red-600">{messageDisplayError}</p>
            ) : null}
            <div
              className={[
                "mt-3 flex flex-wrap gap-2",
                sendWithoutMessage ? "pointer-events-none opacity-50" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {FIELD_TOKENS.map((token) => (
                <button
                  key={token.key}
                  type="button"
                  disabled={sendWithoutMessage}
                  className="rounded-[8px] border border-light-grey px-3 py-1 text-xs text-dark-black disabled:cursor-not-allowed"
                  title={token.help}
                  onMouseDown={(event) => {
                    event.preventDefault();
                  }}
                  onClick={() => insertToken(token.key)}
                >
                  {token.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between border-t border-light-grey pb-2 pt-4">
          <Button
            type="button"
            variant="btn-red"
            size="sm"
            iconSrc="/icons/x.svg"
            iconClassName="h-4 w-4"
            showBefore
            onClick={onClose}
          >
            Dismiss
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="sm"
            iconSrc="/icons/correct.svg"
            showBefore
            disabled={!sendWithoutMessage && !formik.isValid}
          >
            Save
          </Button>
        </div>
      </form>
    </Sidebar>
  );
}
